import { useCallback, type MouseEvent, type ReactElement, type ReactNode } from 'react';
import { Clipboard, Copy, Scissors, TextCursorInput } from 'lucide-react';
import type { PlatformBridge } from '@slate/core';
import {
  editorCopy,
  editorCut,
  editorPaste,
  editorSelectAll,
  useContextMenu,
  useLongPress,
  type ContextMenuEntry,
} from '../context-menu';
import { shortcutLabel } from '../keymap/platform';

export interface EditorContextAreaProps {
  /** Platform bridge — clipboard read/write is routed through it so Cut/Copy/Paste
   * work in WKWebView (desktop) where `execCommand`/`navigator.clipboard` are blocked. */
  platform: PlatformBridge;
  children: ReactNode;
}

/**
 * Wraps the active editor pane (CodeMirror plaintext or Milkdown Crepe) and
 * supplies a Cut/Copy/Paste/Select-all context menu. Clipboard read/write goes
 * through the platform bridge; selection deletion/insertion/select-all use
 * editable-region `execCommand`s. Native menu is already suppressed globally —
 * this region opts in via `onContextMenu` (and long-press on touch).
 */
export function EditorContextArea({ platform, children }: EditorContextAreaProps): ReactElement {
  const { openContextMenu } = useContextMenu();

  // Build the menu by capturing the focused editor element + selection text at
  // OPEN time: the menu steals focus when it mounts, so the actions must close
  // over the editor/selection as they were when the user invoked the menu.
  const buildItems = useCallback((): ContextMenuEntry[] => {
    const editorEl = document.activeElement;
    const selectionText = window.getSelection()?.toString() ?? '';
    return [
      {
        id: 'cut',
        label: 'Cut',
        icon: Scissors,
        shortcut: shortcutLabel('X'),
        onSelect: () => void editorCut(platform, editorEl, selectionText),
      },
      {
        id: 'copy',
        label: 'Copy',
        icon: Copy,
        shortcut: shortcutLabel('C'),
        onSelect: () => void editorCopy(platform, selectionText),
      },
      {
        id: 'paste',
        label: 'Paste',
        icon: Clipboard,
        shortcut: shortcutLabel('V'),
        onSelect: () => void editorPaste(platform, editorEl),
      },
      { type: 'separator', id: 'sep' },
      {
        id: 'select-all',
        label: 'Select all',
        icon: TextCursorInput,
        shortcut: shortcutLabel('A'),
        onSelect: () => editorSelectAll(editorEl),
      },
    ];
  }, [platform]);

  const onContextMenu = useCallback(
    (e: MouseEvent): void => {
      e.preventDefault();
      openContextMenu(e.clientX, e.clientY, buildItems());
    },
    [openContextMenu, buildItems],
  );

  const longPress = useLongPress((x, y) => openContextMenu(x, y, buildItems()));

  return (
    <div className="mp-ctx-region mp-ctx-region--editor" onContextMenu={onContextMenu} {...longPress}>
      {children}
    </div>
  );
}
